import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Shell } from "./components/Shell";
import { LoginPage } from "./pages/Login";
import { StatsPage } from "./pages/Stats";
import { ReportsPage } from "./pages/Reports";
import { FoodsPage } from "./pages/Foods";
import { ScansPage } from "./pages/Scans";
import { BarcodesPage } from "./pages/Barcodes";
import { ComparePage } from "./pages/Compare";
import { EndpointsPage } from "./pages/Endpoints";
import { SettingsPage } from "./pages/Settings";
import { UsersPage } from "./pages/Users";
import { api, clearToken, getToken } from "./lib/api";
import { onUnauthorized } from "./lib/auth-bus";
import type { AdminHealth } from "./lib/types";

type Tab =
  | "stats"
  | "reports"
  | "foods"
  | "scans"
  | "barcodes"
  | "users"
  | "compare"
  | "endpoints"
  | "settings";

function readTabFromHash(): Tab {
  const raw = window.location.hash.replace(/^#\/?/, "").split("?")[0];
  if (
    raw === "stats" ||
    raw === "reports" ||
    raw === "foods" ||
    raw === "scans" ||
    raw === "barcodes" ||
    raw === "users" ||
    raw === "compare" ||
    raw === "endpoints" ||
    raw === "settings"
  ) {
    return raw;
  }
  return "stats";
}

export function App() {
  const qc = useQueryClient();
  const [token, setTokenState] = useState<string | null>(getToken());
  const [tab, setTab] = useState<Tab>(readTabFromHash());

  // Hash-driven routing — gives us deep links + browser back/forward for free.
  useEffect(() => {
    const onHash = () => setTab(readTabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (`#/${tab}` !== window.location.hash) {
      window.location.hash = `#/${tab}`;
    }
  }, [tab]);

  // Global 401 → bounce to login. The api() helper has already wiped
  // localStorage; we just need to drop in-memory token state so React renders
  // <LoginPage /> next pass. Subscribed once at mount.
  useEffect(() => {
    return onUnauthorized(() => {
      setTokenState(null);
      qc.clear();
    });
  }, [qc]);

  // Probe /health-admin once on mount to validate the token. The api() helper
  // handles 401 globally (clears token + emits unauthorized); other failures
  // (network, 5xx) just surface as a query error and don't lock the user out.
  const health = useQuery<AdminHealth>({
    queryKey: ["admin-health"],
    queryFn: () => api<AdminHealth>("/v1/admin/health-admin"),
    enabled: token != null,
    retry: false,
  });

  function signOut() {
    // Best-effort revoke on the server; we don't block the UI on the
    // network round trip. Even if the request fails (offline, server down),
    // wiping the local token still locks the user out client-side.
    api("/v1/admin/logout", { method: "POST" }).catch(() => {});
    clearToken();
    setTokenState(null);
    qc.clear();
  }

  if (!token) {
    return <LoginPage onAuthed={(t) => setTokenState(t)} />;
  }

  return (
    <Shell tab={tab} onTabChange={setTab} env={health.data?.env ?? null}>
      {tab === "stats" && <StatsPage />}
      {tab === "reports" && <ReportsPage />}
      {tab === "foods" && <FoodsPage />}
      {tab === "scans" && <ScansPage />}
      {tab === "barcodes" && <BarcodesPage />}
      {tab === "users" && <UsersPage />}
      {tab === "compare" && <ComparePage />}
      {tab === "endpoints" && <EndpointsPage />}
      {tab === "settings" && <SettingsPage onSignOut={signOut} />}
    </Shell>
  );
}
